import {Trans, useTranslation, TKey} from '@/translations/i18n.tsx'
import {Separator} from '@components/ui/separator'
import {FC} from 'react'
import {Assignment, Compensation} from '@features/assignment/types'
import {getServiceType} from '@features/assignment/utils.tsx'
import {Tooltip} from '@components/ui/tooltip'
import {CurrencyDollarIcon, HelpCircleIcon} from '@components/ui/icon'
import {cn, formatCurrency} from '@utilities/helpers.ts'
import {ASSIGNMENT_CARD_WRAPPER_CLASSNAME, ASSIGNMENT_CARD_TITLE_CLASSNAME} from '@features/assignment/constants'

export const compensationKeyToLabel = {
    photo_session: 'assignment:compensation_card:photo_session',
    travel: 'assignment:compensation_card:travel',
    profile_photo: 'assignment:compensation_card:profile_photo',
    bonus: 'assignment:compensation_card:bonus',
    walkthrough: 'walkthrough:service',
    total: 'commons:total'
} as const satisfies Record<Compensation['compensationKey'], TKey>

interface AssignmentCompensationRowProps {
    currency: Compensation['currency']
    compensationKey: Compensation['compensationKey']
    amount: number
    size?: 'sm' | 'md'
    isBoldAmount?: boolean
    tooltip?: string
}

const AssignmentCompensationRow: FC<AssignmentCompensationRowProps> = ({
    currency,
    compensationKey,
    amount,
    size = 'sm',
    isBoldAmount = false,
    tooltip
}) => {
    const {t} = useTranslation()

    return (
        <div
            className={cn(
                'flex justify-between w-full whitespace-normal [&_svg]:w-5',
                size == 'md' ? 'text-md' : 'text-sm'
            )}
        >
            <div className="flex items-center gap-2">
                <CurrencyDollarIcon />
                <div className="flex items-start gap-1">
                    <p>{t(compensationKeyToLabel[compensationKey])}</p>
                    {!!tooltip && (
                        <Tooltip variant="light" content={<p>{tooltip}</p>}>
                            <HelpCircleIcon color="var(--color-neutral-400)" size={16} />
                        </Tooltip>
                    )}
                </div>
            </div>
            <p className={cn(isBoldAmount ? 'font-medium' : 'font-normal')}>{formatCurrency(amount, currency)}</p>
        </div>
    )
}

interface AssignmentCompensationCardProps {
    assignment: Assignment
}

export const AssignmentCompensationCard: FC<AssignmentCompensationCardProps> = ({assignment}) => {
    const {t} = useTranslation()
    const serviceType = getServiceType(assignment.task_media.name, assignment.task_category.name)
    const isBonusQualifying = assignment.task_category.name == 'Bonus Qualifying'

    return (
        <div className={ASSIGNMENT_CARD_WRAPPER_CLASSNAME}>
            <h3 className={ASSIGNMENT_CARD_TITLE_CLASSNAME}>
                {t('assignment:compensation_card:title', {currency: 'USD'})}
            </h3>

            <AssignmentCompensationRow
                amount={assignment.amount ?? 0}
                compensationKey={serviceType == 'walkthrough' ? 'walkthrough' : 'photo_session'}
                currency="USD"
            />

            {!!assignment.travel_compensation && (
                <AssignmentCompensationRow
                    amount={assignment.travel_compensation}
                    compensationKey="travel"
                    currency="USD"
                />
            )}

            {!!assignment.portrait_bonus_amount && (
                <AssignmentCompensationRow
                    amount={assignment.portrait_bonus_amount}
                    compensationKey="profile_photo"
                    currency="USD"
                />
            )}

            {!!assignment.bonus_amount && (
                <AssignmentCompensationRow
                    amount={assignment.bonus_amount}
                    compensationKey="bonus"
                    currency="USD"
                    tooltip={t(
                        isBonusQualifying
                            ? 'assignment:compensation_card:bonus_tooltip_qualifying'
                            : 'assignment:compensation_card:bonus_tooltip'
                    )}
                />
            )}

            <Separator orientation="horizontal" />

            <AssignmentCompensationRow
                amount={assignment.total_amount || 0}
                compensationKey="total"
                currency="USD"
                size="md"
                isBoldAmount
            />

            {!!assignment.bonus_amount && (
                <p className="text-xs">
                    <Trans
                        i18nKey={
                            isBonusQualifying
                                ? 'assignment:compensation_card:total_compensation_info_bonus_qualifying'
                                : 'assignment:compensation_card:total_compensation_info'
                        }
                        values={{amount: assignment.bonus_amount}}
                        components={{
                            strong: <strong />
                        }}
                    />
                </p>
            )}
        </div>
    )
}
